import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { previewProviderCharges, requestWithdrawalOTP, withdrawGHS, withdrawKES } from "src/services";
import { normalizeMobileMoneyPhone } from "src/helpers/tradeFlow";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const SUPPORTED_CURRENCIES = ["GHS", "KES"] as const;
type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

export function isCurrencyWithdrawable(currency: string): boolean {
  return SUPPORTED_CURRENCIES.includes(currency.toUpperCase() as SupportedCurrency);
}

type WithdrawWalletModalProps = {
  open: boolean;
  handleClose: () => void;
  userData: any;
  walletBalance: string;
  currency: string;
  onSuccess: () => void;
};

const withdrawFns: Record<SupportedCurrency, (payload: any) => Promise<any>> = {
  GHS: withdrawGHS,
  KES: withdrawKES,
};

type Step = "form" | "confirm" | "otp";

type ChargePreview = {
  amount: number;
  charge_amount: number;
  total_amount: number;
  fee_bearer: string;
  fee_applied: boolean;
  charge_percentage?: string;
};

function GHSForm({
  userData,
  walletBalance,
  amount,
  setAmount,
  onSubmit,
  isPending,
}: {
  userData: any;
  walletBalance: string;
  amount: string;
  setAmount: (v: string) => void;
  onSubmit: () => void;
  isPending: boolean;
}) {
  const hasMomo = userData?.payout_momo_number && userData?.payout_momo_network;
  const hasBank = userData?.payout_bank_account_number && userData?.payout_bank_code;
  const payoutPreference = userData?.payout_preference || (hasMomo ? "momo" : hasBank ? "bank" : null);
  const hasPayoutDetails = hasMomo || hasBank;

  const displayDestination = () => {
    if (payoutPreference === "bank" && hasBank) {
      return `${userData.payout_bank_name || "Bank"} - ****${(userData.payout_bank_account_number || "").slice(-4)}`;
    }
    if (hasMomo) {
      const networkNames: Record<string, string> = { MTN: "MTN MoMo", VOD: "Vodafone Cash", AIR: "AirtelTigo" };
      return `${networkNames[userData.payout_momo_network] || userData.payout_momo_network} - ${userData.payout_momo_number}`;
    }
    return "Not configured";
  };

  if (!hasPayoutDetails) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-600 text-sm mb-2">No payout details configured.</p>
        <p className="text-slate-500 text-xs">
          Go to <span className="font-semibold">KYC Verification</span> and add your settlement details (Mobile Money or Bank Account) before withdrawing.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Available balance</p>
          <p className="text-2xl font-bold text-slate-900">GHS {walletBalance}</p>
        </div>
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Payout destination</p>
          <p className="text-sm font-semibold text-slate-800">{displayDestination()}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {userData?.payout_momo_name || userData?.payout_bank_account_name || ""}
          </p>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">
            Amount (GHS)
          </label>
          <Input
            type="number"
            min="1"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="text-lg font-semibold"
          />
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <Dialog.Close asChild>
          <Button type="button" variant="outline" className="flex-1">
            Cancel
          </Button>
        </Dialog.Close>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={isPending || !amount}
          className="flex-1 bg-rose-600 text-white hover:bg-rose-700"
        >
          {isPending ? "Sending OTP..." : "Continue"}
        </Button>
      </div>
    </>
  );
}

function KESForm({
  userData,
  walletBalance,
  amount,
  setAmount,
  phoneNumber,
  setPhoneNumber,
  onSubmit,
  isPending,
}: {
  userData: any;
  walletBalance: string;
  amount: string;
  setAmount: (v: string) => void;
  phoneNumber: string;
  setPhoneNumber: (v: string) => void;
  onSubmit: () => void;
  isPending: boolean;
}) {
  return (
    <>
      <div className="space-y-4">
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Available balance</p>
          <p className="text-2xl font-bold text-slate-900">KES {walletBalance}</p>
        </div>
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Payout method</p>
          <p className="text-sm font-semibold text-slate-800">M-PESA</p>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">
            M-PESA Phone Number
          </label>
          <Input
            type="tel"
            placeholder="e.g. 254712345678"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <p className="text-[10px] text-slate-400 mt-1">Format: 254XXXXXXXXX</p>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">
            Amount (KES)
          </label>
          <Input
            type="number"
            min="1"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="text-lg font-semibold"
          />
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <Dialog.Close asChild>
          <Button type="button" variant="outline" className="flex-1">
            Cancel
          </Button>
        </Dialog.Close>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={isPending || !amount || !phoneNumber}
          className="flex-1 bg-rose-600 text-white hover:bg-rose-700"
        >
          {isPending ? "Sending OTP..." : "Continue"}
        </Button>
      </div>
    </>
  );
}

function OTPStep({
  maskedEmail,
  otp,
  setOtp,
  onSubmit,
  onBack,
  onResend,
  isPending,
  isResending,
}: {
  maskedEmail: string;
  otp: string;
  setOtp: (v: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  onResend: () => void;
  isPending: boolean;
  isResending: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="text-center py-2">
        <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mb-3">
          <svg className="w-6 h-6 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-sm text-slate-600">
          Enter the 8-digit code sent to
        </p>
        <p className="text-sm font-semibold text-slate-800">{maskedEmail}</p>
      </div>

      <div>
        <Input
          type="text"
          inputMode="numeric"
          maxLength={8}
          placeholder="00000000"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
          className="text-center text-2xl font-bold tracking-[0.5em] py-3"
          autoFocus
        />
      </div>

      <button
        type="button"
        onClick={onResend}
        disabled={isResending}
        className="text-xs text-rose-600 hover:text-rose-700 underline mx-auto block"
      >
        {isResending ? "Resending..." : "Resend code"}
      </button>

      <div className="flex gap-3">
        <Button type="button" variant="outline" className="flex-1" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={isPending || otp.length !== 8}
          className="flex-1 bg-rose-600 text-white hover:bg-rose-700"
        >
          {isPending ? "Processing..." : "Confirm Withdrawal"}
        </Button>
      </div>
    </div>
  );
}

function ConfirmStep({
  currency,
  chargePreview,
  onConfirm,
  onBack,
  isPending,
}: {
  currency: string;
  chargePreview: ChargePreview;
  onConfirm: () => void;
  onBack: () => void;
  isPending: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-sm text-slate-500">Amount</span>
          <span className="text-sm font-semibold text-slate-900">
            {currency} {chargePreview.amount.toFixed(2)}
          </span>
        </div>
        {chargePreview.fee_applied && chargePreview.charge_amount > 0 && (
          <div className="flex justify-between">
            <span className="text-sm text-slate-500">
              Fee ({chargePreview.charge_percentage || "0"}%)
            </span>
            <span className="text-sm font-semibold text-rose-600">
              {currency} {chargePreview.charge_amount.toFixed(2)}
            </span>
          </div>
        )}
        <div className="border-t border-slate-200 pt-2 flex justify-between">
          <span className="text-sm font-semibold text-slate-700">Total Debit</span>
          <span className="text-base font-bold text-slate-900">
            {currency} {chargePreview.total_amount.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-slate-500">You receive</span>
          <span className="text-sm font-semibold text-green-700">
            {currency} {chargePreview.amount.toFixed(2)}
          </span>
        </div>
      </div>

      <div className="flex gap-3">
        <Button type="button" variant="outline" className="flex-1" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          disabled={isPending}
          className="flex-1 bg-rose-600 text-white hover:bg-rose-700"
        >
          {isPending ? "Sending OTP..." : "Proceed"}
        </Button>
      </div>
    </div>
  );
}

const WithdrawWalletModal = ({
  open,
  handleClose,
  userData,
  walletBalance,
  currency,
  onSuccess,
}: WithdrawWalletModalProps) => {
  const [step, setStep] = useState<Step>("form");
  const [amount, setAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(userData?.phone_number || "");
  const [otp, setOtp] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [chargePreview, setChargePreview] = useState<ChargePreview | null>(null);

  const cur = currency.toUpperCase() as SupportedCurrency;
  const numericBalance = parseFloat(walletBalance?.replace(/,/g, "") || "0");

  const otpMutation = useMutation({
    mutationFn: () => requestWithdrawalOTP(),
    onSuccess: (data) => {
      setMaskedEmail(data.email || "your email");
      setOtp("");
      setStep("otp");
      toast.success("Verification code sent to your email.");
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || "Failed to send OTP";
      toast.error(message);
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (payload: any) => withdrawFns[cur](payload),
    onSuccess: () => {
      toast.success(`${cur} withdrawal submitted successfully.`);
      resetState();
      onSuccess();
      handleClose();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || "Withdrawal failed";
      toast.error(message);
    },
  });

  const chargeMutation = useMutation({
    mutationFn: () => {
      const provider = cur === "KES" ? "synthera" : "korba";
      return previewProviderCharges(provider, "disbursal", parseFloat(amount));
    },
    onSuccess: (data) => {
      setChargePreview(data);
      setStep("confirm");
    },
    onError: () => {
      setChargePreview({ amount: parseFloat(amount), charge_amount: 0, total_amount: parseFloat(amount), fee_bearer: "platform", fee_applied: false });
      setStep("confirm");
    },
  });

  const resetState = () => {
    setStep("form");
    setAmount("");
    setOtp("");
    setMaskedEmail("");
    setChargePreview(null);
  };

  const buildGHSPayload = (numericAmount: number) => {
    const hasMomo = userData?.payout_momo_number && userData?.payout_momo_network;
    const hasBank = userData?.payout_bank_account_number && userData?.payout_bank_code;
    const payoutPreference = userData?.payout_preference || (hasMomo ? "momo" : hasBank ? "bank" : null);

    if (payoutPreference === "bank" && hasBank) {
      return {
        amount: numericAmount,
        destination_type: "bank",
        network_code: "ISP",
        account_name: userData.payout_bank_account_name || "",
        bank_code: userData.payout_bank_code,
        bank_account_number: userData.payout_bank_account_number,
        bank_account_name: userData.payout_bank_account_name || "",
        recipient_bank_name: userData.payout_bank_name || "",
        description: "GHS wallet withdrawal to bank",
      };
    }
    if (hasMomo) {
      return {
        amount: numericAmount,
        destination_type: "momo",
        network_code: userData.payout_momo_network,
        customer_number: userData.payout_momo_number,
        account_name: userData.payout_momo_name || "",
        description: "GHS wallet withdrawal to MoMo",
      };
    }
    return null;
  };

  const buildKESPayload = (numericAmount: number) => ({
    amount: numericAmount,
    destination_type: "mpesa",
    service_code: "KE_MPESA",
    first_name: userData?.firstname || userData?.first_name || "",
    last_name: userData?.lastname || userData?.last_name || "",
    email: userData?.email || "",
    phone_number: normalizeMobileMoneyPhone(phoneNumber, "KE"),
    description: "KES wallet withdrawal to M-PESA",
  });

  const handlePreviewCharges = () => {
    const numericAmount = parseFloat(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast.error("Enter a valid withdrawal amount.");
      return;
    }
    if (numericAmount > numericBalance) {
      toast.error("Insufficient balance.");
      return;
    }
    if (cur === "KES" && !phoneNumber?.trim()) {
      toast.error("Enter an M-PESA phone number.");
      return;
    }
    if (cur === "GHS") {
      const payload = buildGHSPayload(numericAmount);
      if (!payload) {
        toast.error("No payout details found. Please set up your settlement details in KYC.");
        return;
      }
    }
    chargeMutation.mutate();
  };

  const handleConfirmAndSendOTP = () => {
    otpMutation.mutate();
  };

  const handleWithdraw = () => {
    const numericAmount = parseFloat(amount);
    let payload: any;
    if (cur === "GHS") {
      payload = buildGHSPayload(numericAmount);
    } else if (cur === "KES") {
      payload = buildKESPayload(numericAmount);
    }
    payload.otp = otp;
    withdrawMutation.mutate(payload);
  };

  const handleClose_ = () => {
    resetState();
    handleClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && handleClose_()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-bold text-slate-900">
              {step === "otp" ? "Verify Withdrawal" : step === "confirm" ? "Confirm Withdrawal" : `Withdraw ${cur}`}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition">
                <Cross2Icon className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {step === "form" && cur === "GHS" && (
            <GHSForm
              userData={userData}
              walletBalance={walletBalance}
              amount={amount}
              setAmount={setAmount}
              onSubmit={handlePreviewCharges}
              isPending={chargeMutation.isPending}
            />
          )}
          {step === "form" && cur === "KES" && (
            <KESForm
              userData={userData}
              walletBalance={walletBalance}
              amount={amount}
              setAmount={setAmount}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              onSubmit={handlePreviewCharges}
              isPending={chargeMutation.isPending}
            />
          )}
          {step === "confirm" && chargePreview && (
            <ConfirmStep
              currency={cur}
              chargePreview={chargePreview}
              onConfirm={handleConfirmAndSendOTP}
              onBack={() => setStep("form")}
              isPending={otpMutation.isPending}
            />
          )}
          {step === "otp" && (
            <OTPStep
              maskedEmail={maskedEmail}
              otp={otp}
              setOtp={setOtp}
              onSubmit={handleWithdraw}
              onBack={() => setStep("confirm")}
              onResend={() => otpMutation.mutate()}
              isPending={withdrawMutation.isPending}
              isResending={otpMutation.isPending}
            />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default WithdrawWalletModal;
